<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, ref } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import type { BreadcrumbItem } from "@core/components/layout/header/HeaderPage.vue"
import { useConfigStore } from "@core/stores/config.store"
import { useTaskStore } from "@core/stores/task.store"
import type { Building } from "@types"
import type { Address } from "@types"
import type { FormBuildingAddressContext } from "@core/components/building/form/BuildingAppendForm.vue"
import type { ItemBuildingData } from "@core/components/item/ItemBuilding.vue"
import type { SourceTypeChoice } from "@core/components/source/form/append/machine/source-append.machine"
import type { SourceAppendDonePayload } from "@core/components/source/form/append/source-append.types"

interface AddedSourceItem {
    _id_source: string
    _id?: string
    name?: string
    idsource?: string
    type?: string
    idtype?: string
    operator?: string
    details?: {
        model?: string
    }
    isInvitationSent?: boolean
}

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Ajout d'un bâtiment",
            subtitle: "Information sur le bâtiment",
            subtitleSource: "Ajout d'un compteur",
            addedSourcesTitle: "Sources ajoutées",
            invitationSentLabel: "Demande envoyée au titulaire",
            breadcrumbHome: "Accueil",
            goToDataCollection:
                "Aller directement à la récupération des données",
        },
    },
})

// ================================
// STATE
// ================================
const router = useRouter()
const configStore = useConfigStore()
const taskStore = useTaskStore()
const step = ref<"building" | "source">("building")
const createdBuilding = ref<Building | null>(null)
const createdBuildingAddress = ref<Address | null>(null)
const addedSources = ref<AddedSourceItem[]>([])
const currentSourceAppendState = ref<string>("")

// ================================
// COMPUTED
// ================================
const getBreadcrumb = computed<BreadcrumbItem[]>(() => [
    { label: t("breadcrumbHome"), icon: "pi pi-home", route: "/" },
    { label: t("title") },
])

const getItemBuildingData = computed<ItemBuildingData | null>(() => {
    const building = createdBuilding.value
    if (!building?._id) return null
    return {
        _id: building._id,
        name: building.name,
        niveau: building.infos?.niveau,
        address: {
            street: createdBuildingAddress.value?.street,
            zipcode: createdBuildingAddress.value?.zipcode,
            city: createdBuildingAddress.value?.city,
        },
    }
})

// ================================
// HANDLERS
// ================================
function onDoneForm(context: FormBuildingAddressContext): void {
    createdBuilding.value = context.building
    createdBuildingAddress.value = context.address
    addedSources.value = []
    currentSourceAppendState.value = ""
    if (context.building?._id) {
        step.value = "source"
    }
}

function getSourceTypeFromAppendState(
    state: string
): SourceTypeChoice | undefined {
    if (state === "PdlForm") return "pdl"
    if (state === "PceForm") return "pce"
    if (state === "SensorForm") return "source"
    return undefined
}

function upsertAddedSource(
    source: AddedSourceItem,
    options?: {
        isInvitationSent?: boolean
    }
): void {
    if (!source._id_source) return
    const sourceIndex = addedSources.value.findIndex(
        (item) => item._id_source === source._id_source
    )
    const nextSource: AddedSourceItem = {
        ...source,
        isInvitationSent: options?.isInvitationSent ?? source.isInvitationSent,
    }
    if (sourceIndex >= 0) {
        addedSources.value[sourceIndex] = {
            ...addedSources.value[sourceIndex],
            ...nextSource,
        }
        return
    }
    addedSources.value.unshift(nextSource)
}

function onDoneSource(
    sourceId?: string,
    sourceData?: SourceAppendDonePayload
): void {
    if (!sourceId) return
    const sourceType = getSourceTypeFromAppendState(
        currentSourceAppendState.value
    )
    const consentRequest = sourceData?.consentrequest
    const isInvitationSent =
        sourceData?.isInvitationSent ??
        (Array.isArray(consentRequest)
            ? consentRequest.length > 0
            : !!consentRequest)
    upsertAddedSource(
        {
            _id_source: sourceId,
            idsource: sourceData?.idsource ?? sourceId,
            type: sourceData?.type ?? sourceType,
            idtype: sourceData?.idtype ?? sourceType,
            name: sourceData?.name,
            operator: sourceData?.operator,
            details: sourceData?.details,
            isInvitationSent,
        },
        { isInvitationSent }
    )
}

function onUpdateSourceAppendState(state: string): void {
    currentSourceAppendState.value = state
}

async function onGoToDataCollection(): Promise<void> {
    const id = createdBuilding.value?._id
    if (!id) return

    try {
        await taskStore.launchTask(
            "building.onboarding.user",
            { _id_building: id },
            undefined,
            { preBindTaskChannel: false }
        )
    } finally {
        router.push(`/building/${id}`)
    }
}

onMounted(() => {
    configStore.fetchOperators()
    configStore.fetchSourceModels()
})
</script>

<template>
    <div class="page page-add-building">
        <HeaderPage :title="t('title')" :breadcrumb="getBreadcrumb">
            <div id="back-building-factory" class="flex items-center" />
        </HeaderPage>

        <BaseCard class="max-w-screen-lg w-full mx-auto p-6">
            <template #content>
                <BuildingAppendForm
                    v-if="step === 'building'"
                    @done:form="onDoneForm"
                    class="mx-auto"
                />

                <div v-else class="flex flex-col gap-4 items-center">
                    <div class="text-surface-700 font-medium">
                        {{ t("subtitleSource") }}
                    </div>

                    <ItemBuilding
                        v-if="getItemBuildingData"
                        :building="getItemBuildingData"
                        class="cursor-default!"
                    />

                    <SourceAppendForm
                        :building-id="createdBuilding?._id"
                        :address="createdBuildingAddress"
                        :close-on-done="false"
                        consent-sign-disable-task
                        @done="onDoneSource"
                        @update:state="onUpdateSourceAppendState"
                        class="w-full"
                    />

                    <BaseButton
                        :label="t('goToDataCollection')"
                        text
                        @click="onGoToDataCollection"
                    />

                    <div
                        v-if="addedSources.length"
                        class="w-full flex flex-col gap-3"
                    >
                        <h3 class="text-base font-medium text-surface-800">
                            {{ t("addedSourcesTitle") }}
                        </h3>
                        <ItemSource
                            v-for="source in addedSources"
                            :key="source._id_source"
                            :source="source"
                            :building-id="createdBuilding?._id"
                            :show-toggle-used="false"
                            :show-actions="false"
                            class="w-full"
                        >
                            <template #info>
                                <BaseTag
                                    v-if="source.isInvitationSent"
                                    severity="info"
                                    :value="t('invitationSentLabel')"
                                    rounded
                                />
                            </template>
                        </ItemSource>
                    </div>
                </div>
            </template>
        </BaseCard>
    </div>
</template>
